import { ColorTypes } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import { CellModel, ColumnSource, RequestFilters, RowSource } from "@omegagrid/grid";
import { ToolbarItem } from "@omegagrid/toolbar";
import { NodeSource } from "@omegagrid/tree";
import rp, { GridEditorAdapter, GridEditorOptions } from "@reporting/rp-app-host";
import { AccountingTreeAdapter } from "@reporting/rp-mf-accounting";
import { AccountingRights, Application } from "@reporting/rp-types/core";
import { ClientCredentialsFormVariant } from "./clientCredentialsFormAdapter";

export class ClientCredentialsGridEditorAdapter extends GridEditorAdapter {

	private _columns: ColumnSource[];
	private _scopes: NodeSource[];

	async getColumns(): Promise<ColumnSource[]> {
		if (this._columns) return this._columns;

		this._scopes = Object.keys(AccountingRights).map(r => ({ k: (AccountingRights as any)[r], v: r }));

		this._columns = [{
			data: 'controls',
			t: 'a',
			e: false,
			w: 5 + 20 * 2,
			content: [{
				action: 'editRow',
				title: msg('Edit'),
				icon: 'pen',
				color: ColorTypes.Gray,
			}, {
				action: 'deleteRow',
				title: msg('Delete'),
				icon: 'trash',
				color: ColorTypes.Red,
			}],
			filter: false
		}, {
			data: 'id_application',
			header: '#',
			t: 'n',
			z: '0',
			w: 60,
		}, {
			data: 'client_id',
			header: 'Client ID',
			t: 't',
			w: 280,
		}, {
			data: 'name',
			header: msg('Name'),
			t: 't',
			w: 150,
		}, {
			data: 'accounting',
			header: msg('Company'),
			t: 's',
			w: 150,
			adapter: new AccountingTreeAdapter({
				idGroup: rp.accountingProvider.accounting.id_accounting_group,
				right: AccountingRights.Admin,
			}),
		}, {
			// a group-wide application: its tokens serve every company of the group, the company is only its home
			data: 'group_wide',
			header: msg('Whole group'),
			t: 'b',
			w: 100,
		}, {
			data: '_scope',
			header: msg('Scope'),
			t: 'm',
			w: 200,
			content: this._scopes,
		}, {
			data: 'requires_user',
			header: msg('Requires User'),
			t: 'b',
			w: 130,
		}, {
			data: 'redirect_uri',
			header: msg('Redirect URI'),
			t: 't',
			w: 250,
		}, {
			data: 'date_created',
			header: msg('Created'),
			t: 'dt',
			w: 145,
		}, {
			data: 'who_created',
			header: msg('Created by'),
			t: 't',
			w: 150,
		}, {
			data: 'idz',
			t: 'n',
			hidden: true,
		}, {
			data: 'scope_group',
			t: 'n',
			hidden: true,
		}];
		return this._columns;
	}

	async getRows(filters?: RequestFilters): Promise<RowSource[]> {
		const credentials = await rp.ms('accounting').get<Application[]>(`/clientCredentials`, {
			fields: ['idz', 'id_application', 'name', 'accounting', 'company', 'scope', 'scope_group', 'date_created', 'client_id', 'requires_user', 'redirect_uri', 'who_created'],
		});
		return credentials.map(data => ({
			data: {
				...data,
				accounting: {
					t: 't',
					v: data.accounting,
					dv: `${data.accounting} ${data.company}`
				},
				group_wide: data.scope_group != null,
				_scope: {
					v: (data.scope_array ?? []).map(s => [s, 0, 1]),
					dv: data.scope_array == null
						? msg('User rights')
						: data.scope_array.length
							? data.scope_array.map(s => this._scopes.find(scope => scope.k === s)?.v || s).join(', ')
							: msg('No access'),
				},
			}
		}));
	}

	async delete(data: Application) {
		if (!data?.idz || !data?.accounting) return;
		const command = await rp.host.openMenu({
			content: 'yes_no',
			text: `${msg('Are you sure you want to delete')} '${data.name}'?`,
		});
		if (command?.id != 'Y') return;
		try {
			await rp.ms('accounting').delete(`/clientCredentials/${data.idz}`, { accounting: data.accounting });
		} catch {
			// refused (a group-wide application needs a group admin) - the service has shown its message
			return;
		}
		this.editor.refresh();
	}

	/**
	 * Opens the registration form (variant 'company' or 'group'), or the edit form of an existing
	 * registration — the form reads it back by idz under its home company.
	 */
	private openForm(variant: ClientCredentialsFormVariant, application: Application = null) {
		rp.host.openModalSidebar({
			id: application
				? `admin-clientCredentialsForm-${variant}-${application.idz}-${application.accounting}`
				: `admin-clientCredentialsForm-${variant}`,
			caption: application
				? msg('Edit application')
				: variant === 'group' ? msg('Group application') : msg('Client credentials')
		});
	}

	getOptions(): Partial<GridEditorOptions> {
		const toolbar: ToolbarItem[] = [{
			type: 'button',
			id: 'addClient',
			icon: 'plus',
			color: ColorTypes.Blue,
			text: msg('Application'),
		}];
		// a group-wide application serves every company of the group, so only an administrator of
		// the whole group may register one (the server refuses anyone else)
		if (rp.userProvider.isGroupAdmin()) {
			toolbar.push({
				type: 'button',
				id: 'addGroupClient',
				icon: 'plus',
				color: ColorTypes.Green,
				text: msg('Group application'),
			});
		}

		return this.createOptions({
			colLetters: false,
			columnSorting: true,
			filters: true,
			pageSize: null,
			editable: false,
			lightTheme: null,
			darkTheme: null,
			toolbar: {
				new: { enabled: false },
				save: { enabled: false },
				discard: { enabled: false },
			},
			customToolbar: toolbar,
		});
	}

	async onAction(action: string, cell: CellModel): Promise<void> {
		switch (action) {
			case 'addClient':
				this.openForm('company');
				break;
			case 'addGroupClient':
				this.openForm('group');
				break;
			case 'editRow': {
				const data = cell.row.data as Application;
				this.openForm(data.scope_group != null ? 'group' : 'company', data);
				break;
			}
			case 'deleteRow':
				await this.delete(cell.row.data);
				break;
		}
	}

}
